// import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Button, { ButtonProps } from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/GridLegacy';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import Typography, { TypographyProps } from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

interface PropTypes {
  id: string;
  start: string;
  end: string;
  openEnrollment: string;
  closeEnrollment: string;
  capacity: number;
  observationCapacity: number;
  waitlistCapacity: number;
  waitlistObservationCapacity: number;
  enrollmentPerformanceCount: number;
  enrollmentObservationCount: number;
  waitlistPerformanceCount: number;
  waitlistObservationCount: number;
  instructors: string[];
  location: string;
  showDate: boolean;
  onClickRegister: (id: string, enrollmentType: 'performance_and_observation' | 'observation_only') => void;
  onClickWaitlist: (id: string, enrollmentType: 'performance_and_observation' | 'observation_only') => void;
}

const IconList = styled(List)<{ component?: React.ElementType }>({
  padding: 0,
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: 4,
    color: 'inherit',
  },
  '& .MuiListItem-root': {
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 8,
  },
});

function SelfSchedulePeriod(props: PropTypes): JSX.Element {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'), { defaultMatches: true });
  dayjs.extend(advancedFormat);
  dayjs.extend(duration);
  dayjs.extend(relativeTime);

  // returns the typography component indicating the number of performance seats remaining in the period
  const getPerformanceSeatsRemainingText = () => {
    // props to pass to all typography components
    const baseTextProps: TypographyProps = {
      variant: 'subtitle1',
      lineHeight: 1.25,
      fontWeight: 'bold',
      align: isXs ? 'center' : 'right',
      color: theme.palette.success.main,
    };

    // if there are no performance seats at all return null
    if (props?.capacity <= 0) {
      return null;
    }

    // no seats remaining
    if (props?.enrollmentPerformanceCount >= props?.capacity) {
      return (
        <Typography {...baseTextProps} color={theme.palette.error.main}>
          0 Drive Seats Remaining
        </Typography>
      );
    }

    // 1 seat remaining
    if (props?.capacity - props?.enrollmentPerformanceCount == 1) {
      return <Typography {...baseTextProps}>1 Drive Seat Remaining</Typography>;
    }

    // more than 1 seat remaining
    if (props?.capacity > props?.enrollmentPerformanceCount) {
      return (
        <Typography {...baseTextProps}>
          {props?.capacity - props?.enrollmentPerformanceCount} Drive Seats Remaining
        </Typography>
      );
    }
  };

  // returns the typography component indicating the number of observation seats remaining in the period
  const getObservationSeatsRemainingText = () => {
    // props to pass to all typography components
    const baseTextProps: TypographyProps = {
      variant: 'subtitle1',
      lineHeight: 1.25,
      fontWeight: 'bold',
      align: isXs ? 'center' : 'right',
      color: theme.palette.success.main,
    };

    // if there are no observation seats at all return null
    if (props?.observationCapacity <= 0) {
      return null;
    }

    // no seats remaining
    if (props?.enrollmentObservationCount >= props?.observationCapacity) {
      return (
        <Typography {...baseTextProps} color={theme.palette.error.main}>
          0 Observation Seats Remaining
        </Typography>
      );
    }

    // 1 seat remaining
    if (props?.observationCapacity - props?.enrollmentObservationCount == 1) {
      return <Typography {...baseTextProps}>1 Observation Seat Remaining</Typography>;
    }

    // more than 1 seat remaining
    if (props?.observationCapacity > props?.enrollmentObservationCount) {
      return (
        <Typography {...baseTextProps}>
          {props?.observationCapacity - props?.enrollmentObservationCount} Observation Seats Remaining
        </Typography>
      );
    }
  };

  const getSecondaryText = () => {
    // props to pass to all typography components
    const baseTextProps: TypographyProps = {
      variant: 'subtitle2',
      lineHeight: 1.25,
      align: isXs ? 'center' : 'right',
      color: 'text.secondary',
      pt: 0.5,
    };

    // there are still seats remaining
    if (props?.enrollmentPerformanceCount < props?.capacity && props?.closeEnrollment) {
      return (
        <Typography {...baseTextProps}>
          {`Registration Closes In ${dayjs.duration(dayjs().diff(props?.closeEnrollment)).humanize()}`}
        </Typography>
      );
    }

    // wait list is disabled
    if (props?.waitlistCapacity <= 0) {
      return undefined;
    }

    // wait list is full
    if (props?.waitlistPerformanceCount >= props?.waitlistCapacity) {
      return <Typography {...baseTextProps}>Waitlist Is Full</Typography>;
    }

    // wait list is not full
    if (props?.waitlistPerformanceCount <= 0) {
      return <Typography {...baseTextProps}>Waitlist Is Empty</Typography>;
    } else if (props?.waitlistPerformanceCount == 1) {
      return <Typography {...baseTextProps}>1 Student Already On Waitlist</Typography>;
    } else {
      return (
        <Typography {...baseTextProps}>
          {props?.waitlistPerformanceCount + props?.waitlistObservationCount} Students Already On Waitlist
        </Typography>
      );
    }
  };

  const getPerformanceRegistrationButton = () => {
    // props to pass to all typography components
    const baseButtonProps: ButtonProps = {
      sx: {
        py: 0,
        mt: 1,
      },
      variant: 'outlined',
      fullWidth: true,
    };

    if (props?.capacity <= 0) {
      return null;
    }

    // there are still seats remaining
    if (props?.enrollmentPerformanceCount < props?.capacity && props?.closeEnrollment) {
      return (
        <Button
          {...baseButtonProps}
          onClick={() => props?.onClickRegister(props?.id, 'performance_and_observation')}
          endIcon={<DriveEtaIcon />}
        >
          Register To Drive
        </Button>
      );
    }

    // wait list is disabled
    if (props?.waitlistCapacity <= 0) {
      return (
        <Button {...baseButtonProps} disabled endIcon={<DriveEtaIcon />}>
          Register To Drive
        </Button>
      );
    }

    // wait list is full
    if (props?.waitlistPerformanceCount >= props?.waitlistCapacity) {
      return (
        <Button {...baseButtonProps} disabled color="warning" endIcon={<AccessTimeIcon />}>
          Drive Waitlist Full
        </Button>
      );
    }

    return (
      <Button
        {...baseButtonProps}
        color="warning"
        onClick={() => props?.onClickWaitlist(props?.id, 'performance_and_observation')}
        endIcon={<AccessTimeIcon />}
      >
        Register For Drive Waitlist
      </Button>
    );
  };

  const getObservationRegistrationButton = () => {
    // props to pass to all typography components
    const baseButtonProps: ButtonProps = {
      sx: {
        py: 0,
        mt: 1,
      },
      variant: 'outlined',
      fullWidth: true,
    };

    if (props?.observationCapacity <= 0) {
      return null;
    }

    // there are still seats remaining
    if (props?.enrollmentObservationCount < props?.observationCapacity && props?.closeEnrollment) {
      return (
        <Button
          {...baseButtonProps}
          onClick={() => props?.onClickRegister(props?.id, 'observation_only')}
          endIcon={<VisibilityIcon />}
        >
          Register To Observe
        </Button>
      );
    }

    // wait list is disabled
    if (props?.waitlistObservationCapacity <= 0) {
      return (
        <Button {...baseButtonProps} disabled endIcon={<VisibilityIcon />}>
          Register To Observe
        </Button>
      );
    }

    // wait list is full
    if (props?.waitlistObservationCount >= props?.waitlistObservationCapacity) {
      return (
        <Button {...baseButtonProps} disabled color="warning" endIcon={<AccessTimeIcon />}>
          {' '}
          Register For Observation Waitlist
        </Button>
      );
    }

    return (
      <Button
        {...baseButtonProps}
        color="warning"
        onClick={() => props?.onClickWaitlist(props?.id, 'observation_only')}
        endIcon={<AccessTimeIcon />}
      >
        {' '}
        Register For Observation Waitlist
      </Button>
    );
  };

  return (
    <Paper
      sx={{
        p: 1,
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#1A2027' : '#fff'),
        border: '1px solid black',
      }}
    >
      <Grid container direction="row">
        <Grid item xs={12} sm={6} container direction="column">
          <Grid item pb={1}>
            {props.showDate && (
              <Typography variant="subtitle1" lineHeight={1.25} fontWeight="bold">
                {dayjs(props.start).format('dddd, MMMM Do')}
                <br />
              </Typography>
            )}

            <Typography variant="subtitle1" lineHeight={1.25} fontWeight="bold">
              {`${dayjs(props.start).format('h:mm A')} - ${dayjs(props.end).format('h:mm A')}`}
            </Typography>
          </Grid>

          <Grid item>
            <IconList>
              <ListItem>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText secondary={props?.instructors?.join(', ') ?? 'TBD'} />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <LocationOnIcon />
                </ListItemIcon>
                <ListItemText secondary={props?.location ?? 'TBD'} />
              </ListItem>
            </IconList>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          container
          direction="column"
          justifyContent="space-between"
          alignItems={{
            xs: undefined,
            sm: 'flex-end',
          }}
          flexWrap={'nowrap'}
        >
          <Grid item py={isXs ? 1 : 0}>
            {getPerformanceSeatsRemainingText()}
            {getObservationSeatsRemainingText()}
            {getSecondaryText()}
          </Grid>

          <Grid item>
            <ButtonGroup sx={{ whiteSpace: 'nowrap' }} fullWidth={isXs}>
              {getPerformanceRegistrationButton()}
              {getObservationRegistrationButton()}
            </ButtonGroup>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default SelfSchedulePeriod;
